import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { RotateCcw, Wrench } from 'lucide-react';
import { BaseComponentImpl } from '../../../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate } from '../../../TemplatesEditor/types';
import { generateTimelineActivities } from '../../utils/timelineGenerator';
import { calculateInstallDate } from '../../utils/calculations';
import { resolveComponentSchedule } from '../../../../../Helpers/maintenanceSchedule';
import { Building } from '../../../../../types/protocols';

import { ComponentMaintenanceDataWithDone } from '../../types';
import { TimelineActivity } from '../../utils/timelineGenerator';
import NumberInput from '../../../../../Components/NumberInput';

interface TooltipState {
    content: string;
    x: number;
    y: number;
}

// Timeline configuration constants
const TIMELINE_DEFAULTS = {
    FALLBACK_YEARS: 10,
    MIN_YEARS: 1,
    MAX_YEARS: 100
} as const;

// Year label interval thresholds
const YEAR_LABEL_THRESHOLDS = [
    { maxYears: 10, interval: 1 },
    { maxYears: 20, interval: 2 },
    { maxYears: 40, interval: 5 }
] as const;
const DEFAULT_LABEL_INTERVAL = 10;

/**
 * Groups timeline activities by year
 */
const groupActivitiesByYear = <T extends TimelineActivity>(
    activities: T[]
): Record<number, T[]> => {
    return activities.reduce((acc, activity) => {
        if (!acc[activity.year]) {
            acc[activity.year] = [];
        }
        acc[activity.year].push(activity);
        return acc;
    }, {} as Record<number, T[]>);
};

/**
 * Calculate year label interval based on timeline length
 * Prevents overcrowding of year labels
 */
const getYearLabelInterval = (totalYears: number): number => {
    for (const { maxYears, interval } of YEAR_LABEL_THRESHOLDS) {
        if (totalYears <= maxYears) return interval;
    }
    return DEFAULT_LABEL_INTERVAL;
};

interface ComponentTimelineProps {
    componentKey: string;
    component: BaseComponentImpl;
    lifetime: ComponentLifetime;
    templates: MaintenanceTemplate[];
    draftComponents: { [componentKey: string]: ComponentMaintenanceDataWithDone };
    building: Building | null;
    hasCondition: boolean;
    hasLifetime: boolean;
    highlightedActivity: string | null;
}

/**
 * Timeline visualization component
 * Displays component activities (maintenance, replacement) over lifetime
 */
export const ComponentTimeline: React.FC<ComponentTimelineProps> = ({
    componentKey,
    component,
    lifetime,
    templates,
    draftComponents,
    building,
    hasCondition,
    hasLifetime,
    highlightedActivity
}) => {
    const currentYear = new Date().getFullYear();
    const installDate = calculateInstallDate(component, lifetime, building);

    // Default span: up to the first replacement, by the same rule the timeline draws it with
    const yearsUntilReplacement = useMemo(() => {
        if (!installDate || !lifetime || !building) return TIMELINE_DEFAULTS.FALLBACK_YEARS;

        const schedule = resolveComponentSchedule({
            component,
            entry: draftComponents[component.key] ?? { maintenanceActivities: {} },
            building,
            lifetime,
            typeTemplates: [],
            findTemplate: () => undefined,
            window: { startYear: currentYear, endYear: currentYear + 1 },
            isDraft: true
        });
        const firstReplacementYear = schedule?.replacement?.firstYear;
        if (firstReplacementYear === undefined) return TIMELINE_DEFAULTS.FALLBACK_YEARS;

        return Math.max(1, firstReplacementYear - currentYear);
    }, [installDate, component, lifetime, building, draftComponents, currentYear]);

    // State for controlling how many years ahead to show
    const [yearsToShow, setYearsToShow] = useState<number>(yearsUntilReplacement);

    // Tooltip state for styled tooltips
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);

    // Generate timeline activities (only once, with endYear)
    const endYear = currentYear + yearsToShow;
    const timelineActivities = useMemo(() =>
        generateTimelineActivities(component, lifetime, templates, draftComponents, building, endYear),
        [component, lifetime, templates, draftComponents, building, endYear]
    );

    // Only render if we have the required data
    if (!lifetime || !hasCondition || !hasLifetime) {
        return null;
    }

    // Don't render if no install date or no activities
    if (!installDate || timelineActivities.length === 0) {
        return null;
    }

    const startYear = currentYear;
    const totalYears = yearsToShow;
    const labelInterval = getYearLabelInterval(totalYears);

    const activitiesByYear = groupActivitiesByYear(timelineActivities);

    return (
        <div key={`${componentKey}-timeline`} className="component-timeline-row">
            <div className="timeline-header">
                <h6 className="timeline-title">Komponentens aktiviteter över dess livslängd</h6>
                <div className="timeline-years-selector">
                    <label htmlFor={`years-input-${componentKey}`}>Visa:</label>
                    <NumberInput
                        id={`years-input-${componentKey}`}
                        min={TIMELINE_DEFAULTS.MIN_YEARS}
                        max={TIMELINE_DEFAULTS.MAX_YEARS}
                        step={1}
                        integer
                        value={yearsToShow}
                        onChange={(value) =>
                            setYearsToShow(Math.min(TIMELINE_DEFAULTS.MAX_YEARS, Math.max(TIMELINE_DEFAULTS.MIN_YEARS, value)))
                        }
                        className="timeline-years-input"
                    />
                    <span className="timeline-years-unit">år</span>
                </div>
            </div>

            <div className="timeline-track">
                <div className="timeline-inner">
                    <div className="timeline-line"></div>
                    
                    {/* Year markers */}
                    {Array.from({length: totalYears + 1}, (_, i) => {
                        const year = startYear + i;
                        const isCurrentYear = year === currentYear;
                        const position = totalYears > 0 ? (i / totalYears) * 100 : 0;
                        const isLastYear = i === totalYears;
                        const shouldShowLabel = i % labelInterval === 0 || isCurrentYear || isLastYear;
                        
                        return (
                            <div 
                                key={`year-${year}`}
                                className={`timeline-year-marker ${isCurrentYear ? 'current-year' : ''}`}
                                style={{ left: `${position}%` }}
                            >
                                <div
                                    className="year-dot"
                                    onMouseEnter={!shouldShowLabel ? (e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                            content: year.toString(),
                                            x: rect.right,
                                            y: rect.top + rect.height / 2
                                        });
                                    } : undefined}
                                    onMouseLeave={!shouldShowLabel ? () => setTooltip(null) : undefined}
                                ></div>
                                {shouldShowLabel && <span className="year-label">{year}</span>}
                            </div>
                        );
                    })}
                    
                    {/* Activity markers (maintenance and replacement) */}
                    {Object.entries(activitiesByYear).map(([year, activities]) => {
                        const yearNum = parseInt(year);
                        const position = totalYears > 0 ? ((yearNum - startYear) / totalYears) * 100 : 0;
                        
                        return (
                            <div
                                key={`year-activities-${year}`}
                                className="timeline-year-activities"
                                style={{ left: `${position}%` }}
                            >
                                {activities.map((activity, activityIndex) => {
                                    const isOverdue = activity.year < currentYear;
                                    const isHighlighted = highlightedActivity === activity.templateId;
                                    
                                    const activityTooltip = `${activity.name} (${activity.year})`;
                                    return (
                                        <div
                                            key={`activity-${activity.year}-${activityIndex}`}
                                            className={`timeline-activity-marker ${activity.type} ${isOverdue ? 'overdue' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                            onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                    content: activityTooltip,
                                                    x: rect.right,
                                                    y: rect.top + rect.height / 2
                                                });
                                            }}
                                            onMouseLeave={() => setTooltip(null)}
                                        >
                                            <div 
                                                className="activity-dot"
                                                style={activity.color ? { backgroundColor: activity.color } : {}}
                                            >
                                                {activity.type === 'replacement' ? (
                                                    <RotateCcw size={12} />
                                                ) : activity.count && activity.count > 1 ? (
                                                    <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{activity.count}</span>
                                                ) : (
                                                    <Wrench size={12} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Styled Tooltip Portal */}
            {tooltip && ReactDOM.createPortal(
                <div
                    className="preview-tooltip"
                    style={{
                        position: 'fixed',
                        top: tooltip.y,
                        left: tooltip.x + 12,
                        transform: 'translateY(-50%)'
                    }}
                >
                    {tooltip.content}
                </div>,
                document.body
            )}
        </div>
    );
};
